/**
 * Push Node Component
 *
 * Sends push notifications to users.
 * Receives title and body from upstream Prompt node.
 */

import { memo } from 'react';
import { type NodeProps } from '@xyflow/react';
import { BaseNode } from './BaseNode';
import { useI18n } from '@/i18n';
import type { NodeData, PushTargetType } from '@/types/imageWorkflow';

const getTargetTypeLabels = (t: any): Record<PushTargetType, string> => ({
  follow_start: t('imageWorkflows.nodes.push.followStart'),
  current_user: t('imageWorkflows.nodes.push.currentUser'),
  all_users: t('imageWorkflows.nodes.push.allUsers'),
  specific_users: t('imageWorkflows.nodes.push.specificUsers'),
});

const PushNodeComponent = ({ id, data, selected }: NodeProps) => {
  const { t } = useI18n();
  const nodeData = data as NodeData;
  const targetType = (nodeData.push_target_type || 'follow_start') as PushTargetType;
  const targetUserIds = nodeData.push_target_user_ids || [];
  const pushMode = (nodeData.push_mode as string) || 'quick';
  const TARGET_TYPE_LABELS = getTargetTypeLabels(t);

  return (
    <BaseNode id={id} data={nodeData} type="push" selected={selected} hasSourceHandle={false}>
      <div className="space-y-1">
        {/* Mode */}
        <div className="flex items-center gap-2">
          <span className="text-purple-500">{t('imageWorkflows.nodes.push.mode')}</span>
          <span className="text-purple-700 font-medium">
            {pushMode === 'template'
              ? t('imageWorkflows.nodes.push.templateMode')
              : t('imageWorkflows.nodes.push.quickMode')}
          </span>
        </div>

        {/* Template name (template mode) */}
        {pushMode === 'template' && nodeData.push_template_name && (
          <div className="flex items-center gap-2">
            <span className="text-gray-500">{t('imageWorkflows.nodes.push.template')}</span>
            <span className="text-gray-700 font-medium truncate max-w-[120px]">
              {nodeData.push_template_name as string}
            </span>
          </div>
        )}

        {/* Target Type */}
        <div className="flex items-center gap-2">
          <span className="text-purple-500">{t('imageWorkflows.nodes.push.target')}</span>
          <span className="text-purple-700 font-medium">
            {TARGET_TYPE_LABELS[targetType]}
          </span>
        </div>

        {/* User count for specific_users */}
        {targetType === 'specific_users' && targetUserIds.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-gray-500">{t('imageWorkflows.nodes.push.userCount')}</span>
            <span className="text-gray-700">{targetUserIds.length} {t('imageWorkflows.nodes.push.users')}</span>
          </div>
        )}

        {/* Sound setting */}
        {nodeData.push_sound && nodeData.push_sound !== 'default' && (
          <div className="flex items-center gap-2">
            <span className="text-gray-500">{t('imageWorkflows.nodes.push.sound')}</span>
            <span className="text-gray-700">{nodeData.push_sound}</span>
          </div>
        )}
      </div>
    </BaseNode>
  );
};

export const PushNode = memo(PushNodeComponent);
export default PushNode;
